// Patterns View component
// Displays pattern explorer with filtering, search, and card grid

import { patternsApi, type Pattern, type PatternType, type PatternStatus, type TrendAnalysisResult } from '../services/patterns-api';
import { RadarView } from './RadarView';
import { CombineView } from './CombineView';
import { GalleryTab } from './GalleryTab';
import { PatternModal } from './PatternModal';
import { FilterBar, type FilterBarConfig, type FilterState } from './FilterBar';
import { PATTERNS_FILTERS, PATTERNS_ACTIONS } from '../config/filterbar-config';

export class PatternsView {
  private patterns: Pattern[] = [];
  private currentFilter: PatternType | 'all' = 'all';
  private currentTab: 'explore' | 'radar' | 'combine' | 'gallery' = 'explore';
  private isLoading = false;
  private searchQuery = '';
  private currentFilterState: FilterState | null = null;

  // Sub-views
  private radarView: RadarView | null = null;
  private combineView: CombineView | null = null;
  private galleryTab: GalleryTab | null = null;
  private patternModal: PatternModal | null = null;
  private filterBar: FilterBar | null = null;

  // DOM elements
  private gridEl: HTMLElement;
  private loadingEl: HTMLElement;
  private emptyEl: HTMLElement;

  constructor() {
    this.gridEl = document.getElementById('patterns-grid')!;
    this.loadingEl = document.getElementById('patterns-loading')!;
    this.emptyEl = document.getElementById('patterns-empty')!;

    this.initFilterBar();
    this.setupEventListeners();
  }

  private initFilterBar(): void {
    const config: FilterBarConfig = {
      showSearch: true,
      showApplyButton: true,
      filters: PATTERNS_FILTERS,
      showSort: true,
      customActions: PATTERNS_ACTIONS,
      viewContext: 'patterns',
      title: 'Patterns'
    };

    this.filterBar = new FilterBar('patterns-filter-bar', config);

    // Handle apply
    this.filterBar.onApplyCallback((state) => {
      this.currentFilterState = state;
      this.searchQuery = state.search;

      // Get pattern type filter
      const typeFilter = state.activeFilters.get('patternType');
      if (typeFilter && typeFilter.size > 0) {
        const selectedType = Array.from(typeFilter)[0];
        this.currentFilter = selectedType === 'all' ? 'all' : selectedType as PatternType;
      } else {
        this.currentFilter = 'all';
      }

      this.loadPatterns();
    });

    // Handle real-time search
    this.filterBar.onFilterChangeCallback((state) => {
      this.searchQuery = state.search;
      this.renderPatterns();
    });

    // Handle actions
    this.filterBar.onActionCallback((actionId) => {
      if (actionId === 'refresh') {
        this.loadPatterns();
      } else if (actionId === 'export') {
        this.exportPatterns();
      } else if (actionId === 'analyze-trends') {
        this.analyzeTrends();
      }
    });
  }

  private async analyzeTrends(): Promise<void> {
    // Show loading state
    const btn = document.querySelector('[title="Analyze Trends"]');
    if (btn) {
      btn.classList.add('loading');
      (btn as HTMLElement).style.pointerEvents = 'none';
    }

    try {
      const result = await patternsApi.analyzeTrends();
      this.showTrendResults(result);

      // Refresh patterns list to reflect any status changes
      if (result.changes.length > 0) {
        this.loadPatterns();
      }
    } catch (error) {
      console.error('Failed to analyze trends:', error);
      alert('Failed to analyze trends. Please try again.');
    } finally {
      if (btn) {
        btn.classList.remove('loading');
        (btn as HTMLElement).style.pointerEvents = '';
      }
    }
  }

  private showTrendResults(result: TrendAnalysisResult): void {
    // Create or get modal
    let modal = document.getElementById('trend-results-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'trend-results-modal';
      modal.className = 'trend-results-modal-overlay hidden';
      document.body.appendChild(modal);
    }

    const statusColors: Record<PatternStatus, string> = {
      emerging: '#49e619',
      active: '#60a5fa',
      saturated: '#fbbf24',
      declining: '#ef4444'
    };

    const statusIcons: Record<PatternStatus, string> = {
      emerging: 'trending_up',
      active: 'check_circle',
      saturated: 'warning',
      declining: 'trending_down'
    };

    modal.innerHTML = `
      <div class="trend-results-modal">
        <header class="trend-results-header">
          <div class="trend-results-title">
            <span class="material-symbols-outlined">insights</span>
            <h2>Trend Analysis Results</h2>
          </div>
          <button class="trend-results-close" id="trend-results-close">
            <span class="material-symbols-outlined">close</span>
          </button>
        </header>
        <div class="trend-results-summary">
          <p>${result.summary}</p>
        </div>
        <div class="trend-results-body">
          ${result.changes.length === 0 ? `
            <div class="trend-results-empty">
              <span class="material-symbols-outlined">check_circle</span>
              <p>All patterns are stable. No status changes needed.</p>
            </div>
          ` : `
            <div class="trend-results-list">
              ${result.changes.map(change => `
                <div class="trend-result-item">
                  <div class="trend-result-header">
                    <span class="trend-result-type" style="color: ${this.getTypeColor(change.patternType)}">${change.patternType.toUpperCase()}</span>
                    <span class="trend-result-name">${this.escapeHtml(change.patternName)}</span>
                  </div>
                  <div class="trend-result-change">
                    <span class="trend-status-badge" style="background: ${statusColors[change.previousStatus]}20; color: ${statusColors[change.previousStatus]}">
                      ${change.previousStatus}
                    </span>
                    <span class="material-symbols-outlined">arrow_forward</span>
                    <span class="trend-status-badge" style="background: ${statusColors[change.newStatus]}20; color: ${statusColors[change.newStatus]}">
                      <span class="material-symbols-outlined">${statusIcons[change.newStatus]}</span>
                      ${change.newStatus}
                    </span>
                  </div>
                  <div class="trend-result-reasoning">
                    ${this.escapeHtml(change.reasoning)}
                  </div>
                  <div class="trend-result-metrics">
                    <span>${change.metrics.videoCount} videos</span>
                    <span>${(change.metrics.avgPerformance * 100).toFixed(1)}% perf</span>
                    ${change.metrics.validated ? '<span class="validated">Validated</span>' : ''}
                  </div>
                </div>
              `).join('')}
            </div>
          `}
        </div>
      </div>
    `;

    // Show modal
    modal.classList.remove('hidden');

    // Close handlers
    document.getElementById('trend-results-close')?.addEventListener('click', () => {
      modal?.classList.add('hidden');
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal?.classList.add('hidden');
      }
    });
  }

  private setupEventListeners(): void {
    // Tab switching
    document.querySelectorAll('.patterns-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = (tab as HTMLElement).dataset.patternsTab as 'explore' | 'radar' | 'combine' | 'gallery';
        this.switchTab(tabName);
      });
    });
  }

  private exportPatterns(): void {
    // Export visible patterns as JSON
    const dataStr = JSON.stringify(this.patterns, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `patterns-export-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private switchTab(tab: 'explore' | 'radar' | 'combine' | 'gallery'): void {
    this.currentTab = tab;

    // Update tab buttons
    document.querySelectorAll('.patterns-tab').forEach(t => {
      t.classList.toggle('active', (t as HTMLElement).dataset.patternsTab === tab);
    });

    // Update content sections
    document.querySelectorAll('.patterns-content').forEach(content => {
      content.classList.add('hidden');
    });
    document.getElementById(`patterns-${tab}`)?.classList.remove('hidden');

    // Initialize and load sub-views
    if (tab === 'radar') {
      if (!this.radarView) {
        this.radarView = new RadarView();
      }
      this.radarView.loadPatterns();
    } else if (tab === 'combine') {
      if (!this.combineView) {
        this.combineView = new CombineView();
      }
      this.combineView.loadPatterns();
    } else if (tab === 'gallery') {
      if (!this.galleryTab) {
        this.galleryTab = new GalleryTab();
      }
      this.galleryTab.render();
    }
  }

  async loadPatterns(): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading(true);

    try {
      const query: { type?: PatternType; status?: PatternStatus; minPerformance?: number; limit?: number } = { limit: 50 };

      // Type filter
      if (this.currentFilter !== 'all') {
        query.type = this.currentFilter;
      }

      // Status filter from FilterBar
      if (this.currentFilterState) {
        const statusFilters = this.currentFilterState.activeFilters.get('status');
        if (statusFilters && statusFilters.size === 1) {
          query.status = Array.from(statusFilters)[0] as PatternStatus;
        }

        // Performance filter
        const perfFilter = this.currentFilterState.activeFilters.get('performance');
        if (perfFilter && perfFilter.size > 0) {
          const perf = Array.from(perfFilter)[0];
          if (perf !== 'any') {
            query.minPerformance = parseInt(perf) / 100;
          }
        }
      }

      const result = await patternsApi.listPatterns(query);
      this.patterns = result.patterns;

      // Additional client-side filtering for multiple statuses
      if (this.currentFilterState) {
        const statusFilters = this.currentFilterState.activeFilters.get('status');
        if (statusFilters && statusFilters.size > 1) {
          const statuses = Array.from(statusFilters) as PatternStatus[];
          this.patterns = this.patterns.filter(p => statuses.includes(p.status));
        }
      }

      this.renderPatterns();

      // Update filter bar badge with count
      this.filterBar?.updateBadge(this.patterns.length, 'patterns');
    } catch (error) {
      console.error('Failed to load patterns:', error);
      this.patterns = [];
      this.renderPatterns();
    } finally {
      this.isLoading = false;
      this.showLoading(false);
    }
  }

  private showLoading(show: boolean): void {
    this.loadingEl?.classList.toggle('hidden', !show);
    this.gridEl?.classList.toggle('hidden', show);
  }

  private renderPatterns(): void {
    if (!this.gridEl) return;

    // Filter by search query
    let filtered = this.patterns;
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = this.patterns.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }

    if (filtered.length === 0) {
      this.gridEl.classList.add('hidden');
      this.emptyEl?.classList.remove('hidden');
      return;
    }

    this.emptyEl?.classList.add('hidden');
    this.gridEl.classList.remove('hidden');

    // Separate emerging patterns (for featured card)
    const emerging = filtered.filter(p => p.status === 'emerging');
    const others = filtered.filter(p => p.status !== 'emerging');

    // Render grid
    this.gridEl.innerHTML = '';

    // Featured card for first emerging pattern
    if (emerging.length > 0) {
      const featured = emerging[0];
      this.gridEl.appendChild(this.createFeaturedCard(featured));
    }

    // Regular cards
    const allPatterns = [...emerging.slice(1), ...others];
    allPatterns.forEach(pattern => {
      this.gridEl.appendChild(this.createPatternCard(pattern));
    });
  }

  private createFeaturedCard(pattern: Pattern): HTMLElement {
    const card = document.createElement('div');
    card.className = 'pattern-card pattern-card-featured';
    card.dataset.patternId = pattern.id;
    card.dataset.patternType = pattern.type;

    const statusClass = this.getStatusClass(pattern.status);
    const typeColor = this.getTypeColor(pattern.type);
    const perfPercent = Math.min(pattern.avgPerformance * 100, 100);

    card.innerHTML = `
      <div class="pattern-card-accent"></div>
      <div class="pattern-card-content">
        <div class="pattern-card-header">
          <span class="pattern-type-badge" style="background: ${typeColor}20; color: ${typeColor}">
            ${pattern.type.toUpperCase()}
          </span>
          <div class="pattern-trending">
            <span class="material-symbols-outlined">local_fire_department</span>
            <span>TRENDING</span>
          </div>
        </div>
        <div class="pattern-card-body">
          <h3 class="pattern-name">${this.escapeHtml(pattern.name)}</h3>
          <p class="pattern-description">${this.escapeHtml(pattern.description || '')}</p>
        </div>
        <div class="pattern-card-stats">
          <div class="pattern-stat-label">
            <span>Performance</span>
            <span>${perfPercent.toFixed(1)}%</span>
          </div>
          <div class="pattern-progress-bar">
            <div class="pattern-progress-fill" style="width: ${perfPercent}%"></div>
          </div>
        </div>
        <div class="pattern-card-footer">
          <div class="pattern-status ${statusClass}">
            <span class="status-dot"></span>
            <span>${this.formatStatus(pattern.status)}</span>
          </div>
          <div class="pattern-videos">
            <span class="material-symbols-outlined">play_circle</span>
            <span>${pattern.videoCount} videos</span>
          </div>
        </div>
      </div>
    `;

    card.addEventListener('click', () => this.openPatternDetail(pattern));

    return card;
  }

  private createPatternCard(pattern: Pattern): HTMLElement {
    const card = document.createElement('div');
    card.className = 'pattern-card';
    card.dataset.patternId = pattern.id;
    card.dataset.patternType = pattern.type;

    const statusClass = this.getStatusClass(pattern.status);
    const typeColor = this.getTypeColor(pattern.type);
    const perfPercent = Math.min(pattern.avgPerformance * 100, 100);

    card.innerHTML = `
      <div class="pattern-card-header">
        <span class="pattern-type-badge" style="background: ${typeColor}20; color: ${typeColor}">
          ${pattern.type.toUpperCase()}
        </span>
        <button class="pattern-bookmark">
          <span class="material-symbols-outlined">bookmark_border</span>
        </button>
      </div>
      <div class="pattern-card-body">
        <h3 class="pattern-name">${this.escapeHtml(pattern.name)}</h3>
        <p class="pattern-description">${this.escapeHtml(pattern.description || '')}</p>
      </div>
      <div class="pattern-card-stats">
        <div class="pattern-stat-label">
          <span>Score</span>
          <span>${(perfPercent / 10).toFixed(1)}/10</span>
        </div>
        <div class="pattern-progress-bar">
          <div class="pattern-progress-fill ${this.getProgressClass(pattern.status)}" style="width: ${perfPercent}%"></div>
        </div>
      </div>
      <div class="pattern-card-footer">
        <div class="pattern-status ${statusClass}">
          <span class="status-dot"></span>
          <span>${this.formatStatus(pattern.status)}</span>
        </div>
        <div class="pattern-source">
          <span>${pattern.videoCount} videos</span>
        </div>
      </div>
    `;

    card.addEventListener('click', () => this.openPatternDetail(pattern));

    return card;
  }

  private getTypeColor(type: PatternType): string {
    const colors: Record<PatternType, string> = {
      hook: '#49e619',      // Primary green
      script: '#60a5fa',    // Blue
      visual: '#a78bfa',    // Purple
      cta: '#fbbf24',       // Yellow
      concept: '#f472b6'    // Pink
    };
    return colors[type] || '#9ca3af';
  }

  private getStatusClass(status: PatternStatus): string {
    const classes: Record<PatternStatus, string> = {
      emerging: 'status-emerging',
      active: 'status-active',
      saturated: 'status-saturated',
      declining: 'status-declining'
    };
    return classes[status] || '';
  }

  private getProgressClass(status: PatternStatus): string {
    const classes: Record<PatternStatus, string> = {
      emerging: 'progress-emerging',
      active: 'progress-active',
      saturated: 'progress-saturated',
      declining: 'progress-declining'
    };
    return classes[status] || '';
  }

  private formatStatus(status: PatternStatus): string {
    const labels: Record<PatternStatus, string> = {
      emerging: 'Emerging',
      active: 'Stable',
      saturated: 'Saturated',
      declining: 'Declining'
    };
    return labels[status] || status;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private async openPatternDetail(pattern: Pattern): Promise<void> {
    // Record view action
    try {
      await patternsApi.recordSelection({
        patternId: pattern.id,
        patternType: pattern.type,
        action: 'viewed'
      });
    } catch (e) {
      console.error('Failed to record view:', e);
    }

    // Initialize modal if needed
    if (!this.patternModal) {
      this.patternModal = new PatternModal();

      // Handle save - refresh patterns list
      this.patternModal.onSave((updatedPattern) => {
        // Update pattern in local array
        const index = this.patterns.findIndex(p => p.id === updatedPattern.id);
        if (index !== -1) {
          this.patterns[index] = updatedPattern;
          this.renderPatterns();
        }
      });

      // Handle delete - remove from list
      this.patternModal.onDelete((patternId) => {
        this.patterns = this.patterns.filter(p => p.id !== patternId);
        this.renderPatterns();
      });
    }

    // Open modal with pattern data
    this.patternModal.open(pattern);
  }

  /**
   * Mark a pattern as "used" (when user creates content based on it)
   */
  async markAsUsed(patternId: string, patternType: PatternType, notes?: string): Promise<void> {
    try {
      await patternsApi.recordSelection({
        patternId,
        patternType,
        action: 'used',
        notes
      });
    } catch (e) {
      console.error('Failed to mark as used:', e);
    }
  }
}
